import axios, { AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { all, put, takeLatest, call } from 'redux-saga/effects';

import {
  login,
  loginSuccess,
  loginFailure,
  logOut,
  logOutSuccess,
  googleLogin,
  googleLoginSuccess,
  googleLoginFailure,
  fetchCurrentUser,
  fetchCurrentUserSuccess,
  fetchCurrentUserFailure,
  tokenLogin,
  tokenLoginSuccess,
  tokenLoginFailure,
} from '~/actions';
import { fetchCurrentUserApi } from '~/api';
import { ApiResponseEnum, apiResponseHandler, getMetadataHeaders } from '~/utils/api';
import { getStoredUtmParams } from '~/utils/utm';
import { UserState } from '~/types';

interface ILoginResponse {
  token: string;
  user_business: UserState['businessDetails'] | null;
  first_name: string;
  last_name: string;
  companies: Array<UserState['businessDetails']>;
  current_plan: UserState['currentPlan'];
  email: string;
  user_id: number;
}

export function* loginSaga(action: ReturnType<typeof login>) {
  try {
    const { email, password } = action.payload;

    const response: AxiosResponse<{
      status: boolean;
      data: ILoginResponse | null;
      msg: string;
    }> = yield call(axios.post, `${process.env.SERVER_URL}/api/auth/login/`, {
      email,
      password,
    });

    if (response.data.status && response.data.data) {
      yield put(
        loginSuccess({
          token: response.data.data.token,
          userBusiness: response.data.data.user_business,
          first_name: response.data.data.first_name,
          last_name: response.data.data.last_name,
          email: response.data.data.email,
          companies: response.data.data.companies,
          current_plan: response.data.data.current_plan,
          user_id: response.data.data.user_id,
        }),
      );
    } else {
      yield put(loginFailure());
      toast.error('Invalid email or password.');
    }
  } catch (error) {
    yield put(loginFailure());
    toast.error('An error occurred while logging in. Please try again.');
  }
}

export function* googleLoginSaga(action: ReturnType<typeof googleLogin>) {
  try {
    const { access_token } = action.payload;

    const utmParams = getStoredUtmParams();

    const headers = getMetadataHeaders();
    
    const response: AxiosResponse<{
      status: boolean;
      data: ILoginResponse | null;
      msg: string;
    }> = yield call(axios.post, `${process.env.SERVER_URL}/api/auth/google-auth/`, {
      access_token,
      ...utmParams,
    }, {
      headers,
    });

    if (response.data.status && response.data.data) {
      yield put(
        googleLoginSuccess({
          token: response.data.data.token,
          userBusiness: response.data.data.user_business,
          first_name: response.data.data.first_name,
          last_name: response.data.data.last_name,
          email: response.data.data.email,
          companies: response.data.data.companies,
          current_plan: response.data.data.current_plan,
          user_id: response.data.data.user_id,
        }),
      );
    } else {
      yield put(googleLoginFailure());
      toast.error(response.data.msg || 'Google login failed.');
    }
  } catch (error) {
    yield put(googleLoginFailure());
    toast.error('An error occurred during Google login. Please try again.');
  }
}

export function* fetchCurrentUserSaga(): Generator<any, void, any> {
  try {
    const promise = fetchCurrentUserApi();
    const response = yield call(apiResponseHandler, promise);

    if (response.type === ApiResponseEnum.Success) {
      yield put(fetchCurrentUserSuccess(response.data));
    } else {
      yield put(fetchCurrentUserFailure());
    }
  } catch (error) {
    yield put(fetchCurrentUserFailure());
  }
}

export function* tokenLoginSaga(action: ReturnType<typeof tokenLogin>) {
  try {
    const { token } = action.payload;

    const response: AxiosResponse<{
      status: boolean;
      data: ILoginResponse | null;
      msg: string;
    }> = yield call(axios.get, `${process.env.SERVER_URL}/api/auth/auth-token-login?token=${token}`);

    if (response.data.status && response.data.data) {
      yield put(
        tokenLoginSuccess({
          token: response.data.data.token,
          userBusiness: response.data.data.user_business,
          first_name: response.data.data.first_name,
          last_name: response.data.data.last_name,
          email: response.data.data.email,
          companies: response.data.data.companies,
          current_plan: response.data.data.current_plan,
          user_id: response.data.data.user_id,
        }),
      );
    } else {
      yield put(tokenLoginFailure());
      toast.error(response.data.msg || 'Token login failed.');
    }
  } catch (error) {
    yield put(tokenLoginFailure());
    toast.error('An error occurred during token login. Please try again.');
  }
}

export function* logoutSaga() {
  // TODO: expire token api call
  yield put(logOutSuccess());
}

export default function* root() {
  yield all([
    takeLatest(login.type, loginSaga),
    takeLatest(logOut.type, logoutSaga),
    takeLatest(googleLogin.type, googleLoginSaga),
    takeLatest(fetchCurrentUser.type, fetchCurrentUserSaga),
    takeLatest(tokenLogin.type, tokenLoginSaga),
  ]);
}
